type AssetManifest = {
  entrypoints?: string[];
  files?: Record<string, string>;
};

const STATIC_ENTRYPOINT_PATTERN = /\/static\/(?:css|js)\//;

function toNormalizedPath(value: string): string | null {
  if (!value) {
    return null;
  }

  try {
    const normalized = new URL(value, window.location.origin);
    if (normalized.origin !== window.location.origin) {
      return null;
    }

    return normalized.pathname;
  } catch {
    return null;
  }
}

function normalizeEntrypoints(entrypoints: string[]): string[] {
  return Array.from(
    new Set(
      entrypoints
        .map((entrypoint) => toNormalizedPath(entrypoint))
        .filter((entrypoint): entrypoint is string => Boolean(entrypoint && STATIC_ENTRYPOINT_PATTERN.test(entrypoint)))
        .sort()
    )
  );
}

function getManifestUrl(): URL {
  const publicUrl = process.env.PUBLIC_URL || '';
  const basePath = publicUrl ? `${publicUrl.replace(/\/$/, '')}/asset-manifest.json` : '/asset-manifest.json';
  const manifestUrl = new URL(basePath, window.location.origin);

  manifestUrl.searchParams.set('ts', String(Date.now()));
  return manifestUrl;
}

function getFallbackManifestEntrypoints(manifest: AssetManifest): string[] {
  const fileCandidates = ['runtime-main.js', 'main.css', 'main.js']
    .map((key) => manifest.files?.[key])
    .filter((value): value is string => Boolean(value));

  return normalizeEntrypoints(fileCandidates);
}

export function getCurrentAppAssetSignature(): string | null {
  const scriptPaths = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'))
    .map((script) => script.getAttribute('src') || '')
    .filter(Boolean);

  const stylesheetPaths = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]'))
    .map((link) => link.getAttribute('href') || '')
    .filter(Boolean);

  const signature = normalizeEntrypoints([...scriptPaths, ...stylesheetPaths]).join('|');
  return signature || null;
}

export async function fetchLatestAppAssetSignature(): Promise<string | null> {
  const response = await fetch(getManifestUrl().toString(), {
    cache: 'no-store',
    headers: {
      'cache-control': 'no-store, no-cache, max-age=0',
      pragma: 'no-cache',
    },
  });

  if (!response.ok) {
    return null;
  }

  const manifest = (await response.json()) as AssetManifest;
  const normalizedEntrypoints = normalizeEntrypoints(manifest.entrypoints || []);
  const signature = (normalizedEntrypoints.length > 0
    ? normalizedEntrypoints
    : getFallbackManifestEntrypoints(manifest)).join('|');

  return signature || null;
}
